/**
 * Staff Mailbox — GHL iframe page.
 *
 * Layout:
 *   [Topbar: "Secure Mailbox" + Compose button]
 *   [Left sidebar: InboxList] | [Right panel: ThreadView]
 */

import React, { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../lib/auth-context.js';
import InboxList from './InboxList.js';
import ThreadView from './ThreadView.js';
import ComposeModal from './ComposeModal.js';
import { MessageSummary } from '../../../lib/api.js';

export default function MailboxPage(): React.ReactElement {
  const { loading, error: authError, isStaff } = useAuth();
  const [selectedMessage, setSelected]    = useState<MessageSummary | null>(null);
  const [composeOpen,     setCompose]     = useState(false);
  const [refreshSignal,   setRefresh]     = useState(0);
  const [searchParams,    setSearchParams] = useSearchParams();

  // Deep-link from the contact widget: /app/mailbox?contactId={id}. The inbox
  // list is contact-scoped via this id; a visible banner lets staff clear back
  // to the full inbox.
  const contactFilter = searchParams.get('contactId');
  const clearContactFilter = useCallback(() => {
    searchParams.delete('contactId');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleSelect = useCallback((msg: MessageSummary) => setSelected(msg), []);

  const handleSent = useCallback(() => {
    setCompose(false);
    setRefresh((n) => n + 1); // Trigger inbox refresh
  }, []);

  // Thread read-state changed (opened / replied / manually toggled) → refresh the
  // inbox so the unread dot reflects it.
  const handleThreadChanged = useCallback(() => setRefresh((n) => n + 1), []);

  // Thread trashed from the ThreadView header → close it and refresh the inbox
  // (the default inbox hides trashed threads).
  const handleThreadTrashed = useCallback(() => {
    setSelected(null);
    setRefresh((n) => n + 1);
  }, []);

  // Auth loading
  if (loading) {
    return (
      <div style={styles.center}>
        <p style={styles.muted}>Authenticating…</p>
      </div>
    );
  }

  if (authError || !isStaff) {
    return (
      <div style={styles.center}>
        <p style={{ color: '#c0392b' }}>{authError ?? 'Not authorized. Please reload the page.'}</p>
      </div>
    );
  }

  return (
    <div style={styles.root}>
      {/* Topbar */}
      <div style={styles.topbar}>
        <div style={styles.topbarLeft}>
          <span style={styles.lockIcon}>🔒</span>
          <span style={styles.topbarTitle}>Secure Mailbox</span>
          <span style={styles.encBadge}>End-to-end encrypted</span>
        </div>
        <button style={styles.composeBtn} onClick={() => setCompose(true)}>
          ✉ Compose
        </button>
      </div>

      {/* Contact deep-link filter banner */}
      {contactFilter && (
        <div style={styles.filterBar} role="status">
          <span style={styles.filterText}>
            Showing secure messages for this contact
          </span>
          <button style={styles.filterClear} onClick={clearContactFilter}>
            Show all
          </button>
        </div>
      )}

      {/* Main pane */}
      <div style={styles.main}>
        {/* Inbox list */}
        <div style={styles.sidebar}>
          <InboxList
            selectedId={selectedMessage?.id ?? null}
            onSelect={handleSelect}
            refreshSignal={refreshSignal}
            contactId={contactFilter ?? undefined}
          />
        </div>

        {/* Thread view */}
        <div style={styles.content}>
          {selectedMessage ? (
            <ThreadView
              key={selectedMessage.id}
              messageId={selectedMessage.id}
              initialStarred={!!selectedMessage.starredAt}
              onChanged={handleThreadChanged}
              onTrashed={handleThreadTrashed}
            />
          ) : (
            <div style={styles.empty}>
              <span style={{ fontSize: 40 }}>💬</span>
              <p style={styles.emptyText}>Select a message to read it</p>
            </div>
          )}
        </div>
      </div>

      {/* Compose modal */}
      {composeOpen && (
        <ComposeModal onClose={() => setCompose(false)} onSent={handleSent} />
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    display:       'flex',
    flexDirection: 'column',
    height:        '100%',
    minHeight:     0,
    fontFamily:    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    background:    '#f9fafb',
  },
  topbar: {
    display:        'flex',
    justifyContent: 'space-between',
    alignItems:     'center',
    padding:        '10px 20px',
    background:     '#fff',
    borderBottom:   '1px solid #e5e7eb',
    height:         52,
    flexShrink:     0,
  },
  topbarLeft: {
    display:    'flex',
    alignItems: 'center',
    gap:        10,
  },
  lockIcon:     { fontSize: 18 },
  topbarTitle:  { fontWeight: 600, fontSize: 16, color: '#111827' },
  encBadge: {
    background:   '#ecfdf5',
    color:        '#059669',
    borderRadius: '999px',
    fontSize:     11,
    fontWeight:   500,
    padding:      '2px 10px',
    border:       '1px solid #a7f3d0',
  },
  composeBtn: {
    background:   '#2563eb',
    color:        '#fff',
    border:       'none',
    borderRadius: 6,
    padding:      '8px 18px',
    cursor:       'pointer',
    fontSize:     14,
    fontWeight:   500,
  },
  main: {
    display:  'flex',
    flex:     1,
    overflow: 'hidden',
  },
  sidebar: {
    width:     320,
    flexShrink: 0,
    overflowY: 'hidden',
    display:   'flex',
    flexDirection: 'column',
  },
  content: {
    flex:     1,
    overflow: 'hidden',
    display:  'flex',
    flexDirection: 'column',
  },
  filterBar: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    gap:            12,
    padding:        '8px 20px',
    background:     '#eff6ff',
    borderBottom:   '1px solid #bfdbfe',
    flexShrink:     0,
  },
  filterText: { fontSize: 13, color: '#1e40af', fontWeight: 500 },
  filterClear: {
    background:   'none',
    border:       '1px solid #bfdbfe',
    borderRadius: 6,
    padding:      '4px 10px',
    cursor:       'pointer',
    fontSize:     12,
    fontWeight:   500,
    color:        '#2563eb',
  },
  center: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    height:         '100%',
  },
  muted:  { color: '#9ca3af', fontSize: 14 },
  empty: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    height:         '100%',
    gap:            12,
  },
  emptyText: { color: '#9ca3af', fontSize: 14, margin: 0 },
};
